import React, { useState, useRef, useEffect } from "react";
import {
  Container,
  Navbar,
  Nav,
  Button,
  Form,
  FormControl,
  Offcanvas,
} from "react-bootstrap";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import styles from "./Navbar.module.css";
import { FiSearch, FiPhone, FiChevronLeft, FiGlobe, FiChevronDown } from "react-icons/fi";
import Logo from "../assets/logo.png";
import LogoWhite from "../assets/logowhite.png";
import { IoLogoWindows } from "react-icons/io5";
import { MdOutlineDesktopMac } from "react-icons/md";
import { useAuth } from "../Context/AuthContext";
import { REGISTER_URL, WEB_TRADER_URL, CLIENT_LOGIN_URL, PARTNERS_URL } from "../config/urls";

const CustomNavbar = () => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const [showOffcanvas, setShowOffcanvas] = useState(false);
  const [activeMegaMenu, setActiveMegaMenu] = useState(null);
  const [showMobileMegaMenu, setShowMobileMegaMenu] = useState(false);
  const [mobileMenuTitle, setMobileMenuTitle] = useState("");
  const [mobileMenuContent, setMobileMenuContent] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState(i18n.language || "EN");
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const megaMenuRef = useRef(null);
  const tradingLinkRef = useRef(null);
  const platformsLinkRef = useRef(null);
  const exploreLinkRef = useRef(null);
  const languageRef = useRef(null);
  const location = useLocation();

  const handleClose = () => {
    setShowOffcanvas(false);
    setShowMobileMegaMenu(false);
  };

  const handleShow = () => {
    setShowOffcanvas(true);
    setActiveMegaMenu(null);
  };

  const toggleMegaMenu = (menuName, e) => {
    e.preventDefault();
    setActiveMegaMenu(activeMegaMenu === menuName ? null : menuName);
  };

  const closeMegaMenu = () => {
    setActiveMegaMenu(null);
  };

  const handleLanguageChange = (lang) => {
    setSelectedLanguage(lang);
    setShowLanguageDropdown(false);
    i18n.changeLanguage(lang);
    localStorage.setItem('selectedLanguage', lang);
    
    // Update HTML dir and lang attributes for RTL support
    const isRTL = lang === 'AR' || lang === 'UR';
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = lang.toLowerCase();
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const toggleLanguageDropdown = () => {
    setShowLanguageDropdown(!showLanguageDropdown);
  };

  const handleMobileMegaMenu = (menuName) => {
    if (menuName === "trading") {
      setMobileMenuTitle(t('navbar.trading'));
    } else if (menuName === "explore") {
      setMobileMenuTitle(t('navbar.explore'));
    } else {
      setMobileMenuTitle(t('navbar.platforms'));
    }
    setShowMobileMegaMenu(true);

    if (menuName === "trading") {
      setMobileMenuContent(
        <>
          <div className={styles.mobileMegaMenuColumn}>
            <h3 className={styles.mobileMegaMenuTitle}>{t('navbar.whyTradeWithAbet')}</h3>
            <ul className={styles.mobileMegaMenuList}>
              <li>
                <Link to="/benefits" onClick={handleClose}>
                  {t('navbar.benefits')}
                </Link>
              </li>
              <li>
                <Link to="/promotions" onClick={handleClose}>
                  {t('navbar.promotions')}
                </Link>
              </li>
              <li>
                <Link to="/bonus" onClick={handleClose}>
                  {t('navbar.bonus')}
                </Link>
              </li>
              <li>
                <Link to="/execution-policy" onClick={handleClose}>
                  {t('navbar.executionPolicy')}
                </Link>
              </li>
              <li>
                <Link to="/spreads" onClick={handleClose}>
                  {t('navbar.spreads')}
                </Link>
              </li>
              <li>
                <Link to="/margin-leverage" onClick={handleClose}>
                  {t('navbar.margin')}
                </Link>
              </li>
            </ul>
          </div>
          <div className={styles.mobileMegaMenuColumn}>
            <h3 className={styles.mobileMegaMenuTitle}>{t('navbar.accountTypes')}</h3>
            <ul className={styles.mobileMegaMenuList}>
              <li>
                <Link to="/micro" onClick={handleClose}>
                  {t('navbar.micro')}
                </Link>
              </li>
              <li>
                <Link to="/raw-spread" onClick={handleClose}>
                  {t('navbar.rawSpread')}
                </Link>
              </li>
              <li>
                <Link to="/standard" onClick={handleClose}>
                  {t('navbar.standard')}
                </Link>
              </li>
              <li>
                <Link to="/prime" onClick={handleClose}>
                  {t('navbar.prime')}
                </Link>
              </li>
            </ul>
          </div>
          <div className={styles.mobileMegaMenuColumn}>
            <h3 className={styles.mobileMegaMenuTitle}>{t('navbar.instruments')}</h3>
            <ul className={styles.mobileMegaMenuList}>
              <li>
                <Link to="/forex" onClick={handleClose}>
                  {t('navbar.forex')}
                </Link>
              </li>
              <li>
                <Link to="/indices" onClick={handleClose}>
                  {t('navbar.indices')}
                </Link>
              </li>
              <li>
                <Link to="/energies" onClick={handleClose}>
                  {t('navbar.energies')}
                </Link>
              </li>
              <li>
                <Link to="/metals" onClick={handleClose}>
                  {t('navbar.metals')}
                </Link>
              </li>
              <li>
                <Link to="/cryptocurrency" onClick={handleClose}>
                  {t('navbar.cryptocurrency')}
                </Link>
              </li>
            </ul>
          </div>
          <div className={styles.mobileMegaMenuColumn}>
            <h3 className={styles.mobileMegaMenuTitle}>{t('navbar.platformsColumn')}</h3>
            <ul className={styles.mobileMegaMenuList}>
              <li>
                <Link to="/meta-trader-5" onClick={handleClose}>
                  {t('navbar.metatrader5')}
                </Link>
              </li>
              <li>
                <a
                  href={WEB_TRADER_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleClose}
                >
                  {t("navbar.webTrader")}
                </a>
              </li>
            </ul>
          </div>
        </>
      );
    } else if (menuName === "explore") {
      setMobileMenuContent(
        <>
          <div className={styles.mobileMegaMenuColumn}>
            <h3 className={styles.mobileMegaMenuTitle}>{t('navbar.tradingTools')}</h3>
            <ul className={styles.mobileMegaMenuList}>
              <li>
                <Link to="/economic-calendar" onClick={handleClose}>
                  {t('navbar.economicCalendar')}
                </Link>
              </li>
              <li>
                <Link to="/trading-calculators" onClick={handleClose}>
                  {t('navbar.tradingCalculators')}
                </Link>
              </li>
            </ul>
          </div>
          <div className={styles.mobileMegaMenuColumn}>
            <h3 className={styles.mobileMegaMenuTitle}>{t('navbar.education')}</h3>
            <ul className={styles.mobileMegaMenuList}>
              <li>
                <Link to="/ebooks" onClick={handleClose}>
                  {t('navbar.ebooks')}
                </Link>
              </li>
              <li>
                <Link to="/beginner-guides" onClick={handleClose}>
                  {t('navbar.beginnerGuides')}
                </Link>
              </li>
            </ul>
          </div>
        </>
      );
    } else {
      setMobileMenuContent(
        <>
          <div className={styles.mobileMegaMenuColumn}>
            <ul className={styles.mobileMegaMenuList}>
              <li>
                <Link to="/about" onClick={handleClose}>
                  {t('navbar.companyProfile')}
                </Link>
              </li>
              <li>
                <Link to="/trusted-safety" onClick={handleClose}>
                  {t('navbar.regulationSafety')}
                </Link>
              </li>
              <li>
                <Link to="/awards-certificates" onClick={handleClose}>
                  {t('navbar.awardsCertificates')}
                </Link>
              </li>
            </ul>
          </div>
        </>
      );
    }
  };

  // Close mega menu when clicking outside or route changes
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        megaMenuRef.current &&
        !megaMenuRef.current.contains(event.target) &&
        tradingLinkRef.current &&
        !tradingLinkRef.current.contains(event.target) &&
        platformsLinkRef.current &&
        !platformsLinkRef.current.contains(event.target) &&
        exploreLinkRef.current &&
        !exploreLinkRef.current.contains(event.target)
      ) {
        closeMegaMenu();
      }
      
      // Close language dropdown when clicking outside
      if (languageRef.current && !languageRef.current.contains(event.target)) {
        setShowLanguageDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Close mega menu when route changes
  useEffect(() => {
    closeMegaMenu();
    handleClose();
  }, [location.pathname]);

  const languages = [
    { code: 'EN', name: 'English' },
    { code: 'AR', name: 'العربية' },
    { code: 'UR', name: 'اردو' },
    { code: 'FR', name: 'Français' },
    { code: 'DE', name: 'Deutsch' },
    { code: 'ES', name: 'Español' },
    { code: 'ZH', name: '中文' },
  ];

  return (
    <>
      {/* Top Contact Bar */}
      <div className={styles.topContactBar}>
        <Container>
          <div className="row align-items-center">
            <div className="col-md-6">
              <div className={styles.contactInfo}>
                <FiPhone className={styles.contactIcon} />
                <a href={`tel:${t('navbar.phoneNumber').replace(/\s+/g, '')}`} className={styles.phoneLink}>
                  {t('navbar.phoneNumber')}
                </a>
                <span style={{ margin: '0 5px' }}>|</span>
                <a href="/contact" className={styles.contactLink}>
                  {t('navbar.contactUs')}
                </a>
              </div>
            </div>
            <div className="col-md-6">
              <div className={styles.topLinks}>
                <div className={styles.languageSelector} ref={languageRef}>
                  <button
                    className={styles.languageButton}
                    onClick={toggleLanguageDropdown}
                  >
                    <FiGlobe className={styles.languageIcon} />
                    <span>{selectedLanguage}</span>
                    <FiChevronDown 
                      className={styles.chevronIcon}
                      style={{ 
                        transform: showLanguageDropdown ? 'rotate(180deg)' : 'rotate(0deg)' 
                      }}
                    />
                  </button>
                  {showLanguageDropdown && (
                    <div className={styles.languageDropdown}>
                      {languages.map((lang) => (
                        <button
                          key={lang.code}
                          className={`${selectedLanguage === lang.code ? styles.active : ''}`}
                          onClick={() => handleLanguageChange(lang.code)}
                        >
                          {lang.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <a
                  href={CLIENT_LOGIN_URL}
                  className={styles.clientLoginTop}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('navbar.clientLogin')}
                </a>
                <a
                  href={PARTNERS_URL}
                  className={styles.clientLoginTop}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('navbar.becomePartner')}
                </a>
              </div>
            </div>
          </div>
        </Container>
      </div>

      {/* Main Navbar + Trading Mega Menu wrapper - sticky so dropdown stays correct on scroll */}
      <div className={styles.navbarStickyWrapper}>
      <Navbar expand="lg" className={styles.mainNavbar}>
        <Container>
          <Navbar.Brand as={Link} to="/" className={styles.brand}>
            <img src={LogoWhite} alt="ABET LLC" />
          </Navbar.Brand>

          <Navbar.Toggle
            aria-controls="navbarScroll"
            onClick={handleShow}
            className={styles.navbarToggle}
          />

          <Navbar.Offcanvas
            id="offcanvasNavbar"
            aria-labelledby="offcanvasNavbarLabel"
            placement="end"
            show={showOffcanvas}
            onHide={handleClose}
            className={styles.offcanvas}
          >
            <Offcanvas.Header closeButton className={styles.offcanvasHeader}>
              <Offcanvas.Title
                id="offcanvasNavbarLabel"
                className={styles.offcanvasTitle}
              >
                <img src={LogoWhite} alt="ABET LLC" />
              </Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
              <Nav
                className={`justify-content-end flex-grow-1 pe-3 ${styles.navLinks}`}
              >
                <Nav.Link
                  as={NavLink}
                  to="/"
                  className={styles.navLink}
                  onClick={() => {
                    handleClose();
                    closeMegaMenu();
                  }}
                  onMouseEnter={() => {
                    closeMegaMenu();
                  }}
                >
                  {t('navbar.home')}
                </Nav.Link>
                <div ref={platformsLinkRef} className={styles.navItemWithDropdown}>
                  <Nav.Link
                    as={NavLink}
                    to="#"
                    className={styles.navLink}
                    onClick={(e) => {
                      if (window.innerWidth < 992) {
                        e.preventDefault();
                        handleMobileMegaMenu("platforms");
                      } else {
                        toggleMegaMenu("platforms", e);
                      }
                    }}
                    onMouseEnter={() => {
                      if (window.innerWidth >= 992) {
                        setActiveMegaMenu("platforms");
                      }
                    }}
                  >
                    {t('navbar.platforms')}
                  </Nav.Link>
                  {/* ABET Dropdown - rendered below ABET link */}
                  {activeMegaMenu === "platforms" && (
                    <div
                      className={styles.abetDropdown}
                      onMouseLeave={closeMegaMenu}
                      ref={megaMenuRef}
                    >
                      <ul className={styles.abetDropdownList}>
                        <li>
                          <Link to="/about" onClick={closeMegaMenu}>
                            {t('navbar.companyProfile')}
                          </Link>
                        </li>
                        <li>
                          <Link to="/trusted-safety" onClick={closeMegaMenu}>
                            {t('navbar.regulationSafety')}
                          </Link>
                        </li>
                        <li>
                          <Link to="/awards-certificates" onClick={closeMegaMenu}>
                            {t('navbar.awardsCertificates')}
                          </Link>
                        </li>
                      </ul>
                    </div>
                  )}
                </div>
                <div ref={tradingLinkRef}>
                  <Nav.Link
                    as={NavLink}
                    to="#"
                    className={styles.navLink}
                    onClick={(e) => {
                      if (window.innerWidth < 992) {
                        e.preventDefault();
                        handleMobileMegaMenu("trading");
                      } else {
                        toggleMegaMenu("trading", e);
                      }
                    }}
                    onMouseEnter={() => {
                      if (window.innerWidth >= 992) {
                        setActiveMegaMenu("trading");
                      }
                    }}
                  >
                    {t('navbar.trading')}
                  </Nav.Link>
                </div>
                <div ref={exploreLinkRef}>
                  <Nav.Link
                    as={NavLink}
                    to="#"
                    className={styles.navLink}
                    onClick={(e) => {
                      if (window.innerWidth < 992) {
                        e.preventDefault();
                        handleMobileMegaMenu("explore");
                      } else {
                        toggleMegaMenu("explore", e);
                      }
                    }}
                    onMouseEnter={() => {
                      if (window.innerWidth >= 992) {
                        setActiveMegaMenu("explore");
                      }
                    }}
                  >
                    {t('navbar.explore')}
                  </Nav.Link>
                </div>
                {/* Partnership links temporarily hidden */}
                {/* <Nav.Link
                  as={NavLink}
                  to="/partnership"
                  className={styles.navLink}
                  onClick={() => {
                    handleClose();
                    closeMegaMenu();
                  }}
                  onMouseEnter={() => {
                    closeMegaMenu();
                  }}
                >
                  {t('navbar.partnership')}
                </Nav.Link> */}
                <Nav.Link
                  as={NavLink}
                  to="/blogs"
                  className={styles.navLink}
                  onClick={() => {
                    handleClose();
                    closeMegaMenu();
                  }}
                  onMouseEnter={() => {
                    closeMegaMenu();
                  }}
                >
                  {t('navbar.blogs')}
                </Nav.Link>
                <Nav.Link
                  as={NavLink}
                  to="/contact"
                  className={styles.navLink}
                  onClick={() => {
                    handleClose();
                    closeMegaMenu();
                  }}
                  onMouseEnter={() => {
                    closeMegaMenu();
                  }}
                >
                  {t('navbar.contact')}
                </Nav.Link>
                {/* <Nav.Link as={NavLink} to="/partner" className={styles.navLink}>
                  {t('navbar.becomePartner')}
                </Nav.Link> */}
              </Nav>
            </Offcanvas.Body>
          </Navbar.Offcanvas>
        </Container>
      </Navbar>

      {/* Trading Mega Menu - inside sticky wrapper so it stays below navbar on scroll */}
      {activeMegaMenu === "trading" && (
        <div
          className={styles.tradingMegaMenuOuter}
          onMouseLeave={closeMegaMenu}
          ref={megaMenuRef}
        >
        <Container className={styles.container}>
          <div className={styles.megaMenu}>
            <div className="row">
              <div className="col-md">
                <div className={styles.megaMenuColumn}>
                  <img
                    src={Logo}
                    alt="ABET LLC"
                    className={styles.megaMenuLogo}
                  />
                  <div className={styles.megaMenuContent}>
                    {t('navbar.megaMenuDescription')}
                  </div>
                  <Button
                    as="a"
                    href={REGISTER_URL}
                    className={styles.megaMenuButton}
                    onClick={closeMegaMenu}
                  >
                    {t('common.openAccount')}
                  </Button>
                </div>
              </div>
              <div className="col-md">
                <div className={styles.megaMenuColumn}>
                  <h3 className={styles.megaMenuTitle}>{t('navbar.whyTradeWithAbet')}</h3>
                  <ul className={styles.megaMenuList}>
                    <li>
                      <Link to="/benefits" onClick={closeMegaMenu}>
                        {t('navbar.benefits')}
                      </Link>
                    </li>
                    <li>
                      <Link to="/promotions" onClick={closeMegaMenu}>
                        {t('navbar.promotions')}
                      </Link>
                    </li>
                    <li>
                      <Link to="/bonus" onClick={closeMegaMenu}>
                        {t('navbar.bonus')}
                      </Link>
                    </li>
                    <li>
                      <Link to="/execution-policy" onClick={closeMegaMenu}>
                        {t('navbar.executionPolicy')}
                      </Link>
                    </li>
                    <li>
                      <Link to="/spreads" onClick={closeMegaMenu}>
                        {t('navbar.spreads')}
                      </Link>
                    </li>
                    <li>
                      <Link to="/margin-leverage" onClick={closeMegaMenu}>
                        {t('navbar.margin')}
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="col-md">
                <div className={styles.megaMenuColumn}>
                  <h3 className={styles.megaMenuTitle}>
                    <Link to="/account-type">{t('navbar.accountTypes')}</Link>
                  </h3>
                  <ul className={styles.megaMenuList}>
                    <li>
                      <Link to="/micro" onClick={closeMegaMenu}>
                        {t('navbar.micro')}
                      </Link>
                    </li>
                    <li>
                      <Link to="/raw-spread" onClick={closeMegaMenu}>
                        {t('navbar.rawSpread')}
                      </Link>
                    </li>
                    <li>
                      <Link to="/standard" onClick={closeMegaMenu}>
                        {t('navbar.standard')}
                      </Link>
                    </li>
                    <li>
                      <Link to="/prime" onClick={closeMegaMenu}>
                        {t('navbar.prime')}
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="col-md">
                <div className={styles.megaMenuColumn}>
                  <h3 className={styles.megaMenuTitle}>{t('navbar.instruments')}</h3>
                  <ul className={styles.megaMenuList}>
                    <li>
                      <Link to="/forex" onClick={closeMegaMenu}>
                        {t('navbar.forex')}
                      </Link>
                    </li>
                    <li>
                      <Link to="/indices" onClick={closeMegaMenu}>
                        {t('navbar.indices')}
                      </Link>
                    </li>
                    <li>
                      <Link to="/energies" onClick={closeMegaMenu}>
                        {t('navbar.energies')}
                      </Link>
                    </li>
                    <li>
                      <Link to="/metals" onClick={closeMegaMenu}>
                        {t('navbar.metals')}
                      </Link>
                    </li>
                    <li>
                      <Link to="/cryptocurrency" onClick={closeMegaMenu}>
                        {t('navbar.cryptocurrency')}
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="col-md">
                <div className={styles.megaMenuColumn}>
                  <h3 className={styles.megaMenuTitle}>{t('navbar.platformsColumn')}</h3>
                  <ul className={styles.megaMenuList}>
                    <li>
                      <Link to="/meta-trader-5" onClick={closeMegaMenu}>
                        {t('navbar.metatrader5')}
                      </Link>
                    </li>
                    <li>
                      <a
                        href={WEB_TRADER_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={closeMegaMenu}
                      >
                        {t("navbar.webTrader")}
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </Container>
        </div>
      )}

      {/* Explore Mega Menu - inside sticky wrapper so it stays below navbar on scroll */}
      {activeMegaMenu === "explore" && (
        <div
          className={styles.tradingMegaMenuOuter}
          onMouseLeave={closeMegaMenu}
          ref={megaMenuRef}
        >
        <Container className={styles.container}>
          <div className={styles.megaMenu}>
            <div className="row">
              <div className="col-md-4">
                <div className={styles.megaMenuColumn}>
                  <img
                    src={Logo}
                    alt="ABET LLC"
                    className={styles.megaMenuLogo}
                  />
                  <div className={styles.megaMenuContent}>
                    {t('navbar.megaMenuDescription')}
                  </div>
                  <Button
                    as="a"
                    href={REGISTER_URL}
                    className={styles.megaMenuButton}
                    onClick={closeMegaMenu}
                  >
                    {t('common.openAccount')}
                  </Button>
                </div>
              </div>
              <div className="col-md-4">
                <div className={styles.megaMenuColumn}>
                  <h3 className={styles.megaMenuTitle}>{t('navbar.tradingTools')}</h3>
                  <ul className={styles.megaMenuList}>
                    <li>
                      <Link to="/economic-calendar" onClick={closeMegaMenu}>
                        {t('navbar.economicCalendar')}
                      </Link>
                    </li>
                    <li>
                      <Link to="/trading-calculators" onClick={closeMegaMenu}>
                        {t('navbar.tradingCalculators')}
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="col-md-4">
                <div className={styles.megaMenuColumn}>
                  <h3 className={styles.megaMenuTitle}>{t('navbar.education')}</h3>
                  <ul className={styles.megaMenuList}>
                    <li>
                      <Link to="/ebooks" onClick={closeMegaMenu}>
                        {t('navbar.ebooks')}
                      </Link>
                    </li>
                    <li>
                      <Link to="/beginner-guides" onClick={closeMegaMenu}>
                        {t('navbar.beginnerGuides')}
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </Container>
        </div>
      )}
      </div>

      {/* Mobile Mega Menu Offcanvas */}
      <Offcanvas
        show={showMobileMegaMenu}
        onHide={() => setShowMobileMegaMenu(false)}
        placement="end"
        className={styles.mobileMegaMenuCanvas}
      >
        <Offcanvas.Header className={styles.mobileMegaMenuHeader}>
          <Button
            variant="link"
            onClick={() => setShowMobileMegaMenu(false)}
            className={styles.mobileMegaMenuBackButton}
          >
            <FiChevronLeft size={24} />
          </Button>
          <Offcanvas.Title className={styles.mobileMegaMenuTitle}>
            {mobileMenuTitle}
          </Offcanvas.Title>
          <div
            className={styles.mobileMegaMenuCloseButton}
            onClick={handleClose}
          >
            &times;
          </div>
        </Offcanvas.Header>
        <Offcanvas.Body className={styles.mobileMegaMenuBody}>
          {mobileMenuContent}
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
};

export default CustomNavbar;
